import React, { useState, useEffect } from 'react';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { FilterMatchMode, FilterOperator } from 'primereact/api';
import { Dropdown } from 'primereact/dropdown';
import { Chart } from 'primereact/chart';

// Mock data for demonstration
const mockSummaries = [
    {
        id: 1,
        period: "2025-Q1",
        totalOrders: 125,
        completedOrders: 98,
        pendingOrders: 22,
        cancelledOrders: 5,
        totalAmount: 1250000,
        averageOrderValue: 10000,
        topCustomer: "Mega Motor A.Ş.",
        topProduct: "Şanzıman Dişlisi"
    },
    {
        id: 2,
        period: "2025-Q2",
        totalOrders: 145,
        completedOrders: 110,
        pendingOrders: 30,
        cancelledOrders: 5,
        totalAmount: 1450000,
        averageOrderValue: 10000,
        topCustomer: "Anadolu Otomotiv Ltd.",
        topProduct: "Fren Balataları"
    },
    {
        id: 3,
        period: "2025-Q3",
        totalOrders: 160,
        completedOrders: 120,
        pendingOrders: 35,
        cancelledOrders: 5,
        totalAmount: 1600000,
        averageOrderValue: 10000,
        topCustomer: "Yıldız Makine San.",
        topProduct: "Motor Valfleri"
    }
];

const periodOptions = [
    { name: '2025-Q1', code: '2025-Q1' },
    { name: '2025-Q2', code: '2025-Q2' },
    { name: '2025-Q3', code: '2025-Q3' },
    { name: '2025-Q4', code: '2025-Q4' }
];

const OrderSummaryPage = () => {
    const [summaries, setSummaries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [globalFilterValue, setGlobalFilterValue] = useState('');
    const [filters, setFilters] = useState({
        global: { value: null, matchMode: FilterMatchMode.CONTAINS },
        period: { value: null, matchMode: FilterMatchMode.EQUALS },
        topCustomer: { 
            operator: FilterOperator.AND,
            constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] 
        },
        topProduct: { 
            operator: FilterOperator.AND,
            constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] 
        }
    });
    const [chartData, setChartData] = useState({
        labels: [],
        datasets: []
    });
    const [chartOptions, setChartOptions] = useState({});

    useEffect(() => {
        setSummaries(mockSummaries);
        setLoading(false);

        // Prepare chart data
        const labels = mockSummaries.map(item => item.period);
        const completedData = mockSummaries.map(item => item.completedOrders);
        const pendingData = mockSummaries.map(item => item.pendingOrders);
        const cancelledData = mockSummaries.map(item => item.cancelledOrders);
        const totalAmountData = mockSummaries.map(item => item.totalAmount / 10000); // Scaled for better visualization

        setChartData({
            labels: labels,
            datasets: [
                {
                    label: 'Tamamlanan Siparişler',
                    backgroundColor: '#22C55E',
                    data: completedData
                },
                {
                    label: 'Bekleyen Siparişler',
                    backgroundColor: '#F59E0B',
                    data: pendingData
                },
                {
                    label: 'İptal Edilen Siparişler',
                    backgroundColor: '#EF4444',
                    data: cancelledData
                },
                {
                    label: 'Toplam Tutar (10,000 TL)',
                    backgroundColor: '#3B82F6',
                    data: totalAmountData,
                    type: 'line',
                    yAxisID: 'y1'
                }
            ]
        });

        setChartOptions({
            maintainAspectRatio: false,
            aspectRatio: 1.5,
            plugins: {
                legend: {
                    position: 'top',
                    labels: {
                        font: {
                            size: 14
                        }
                    }
                },
                tooltip: {
                    mode: 'index',
                    intersect: false
                }
            },
            scales: {
                x: {
                    ticks: {
                        font: {
                            size: 14
                        }
                    },
                    grid: {
                        display: false
                    }
                },
                y: {
                    type: 'linear',
                    display: true,
                    position: 'left',
                    ticks: {
                        font: {
                            size: 14
                        }
                    },
                    title: {
                        display: true,
                        text: 'Sipariş Sayısı',
                        font: {
                            size: 14
                        }
                    }
                },
                y1: {
                    type: 'linear',
                    display: true,
                    position: 'right',
                    ticks: {
                        font: {
                            size: 14
                        }
                    },
                    title: {
                        display: true,
                        text: 'Toplam Tutar (10,000 TL)',
                        font: {
                            size: 14
                        }
                    },
                    grid: {
                        drawOnChartArea: false
                    }
                }
            }
        });
    }, []);

    const onGlobalFilterChange = (e) => {
        const value = e.target.value;
        let _filters = { ...filters };
        
        _filters['global'].value = value;
        
        setFilters(_filters);
        setGlobalFilterValue(value);
    };

    const header = (
        <div className="flex justify-content-between align-items-center">
            <div className="flex align-items-center">
                <span className="p-input-icon-left">
                    <i className="pi pi-search" />
                    <InputText 
                        value={globalFilterValue} 
                        onChange={onGlobalFilterChange} 
                        placeholder="Ara..." 
                    />
                </span>
            </div>
            <div className="flex gap-2">
                <Button 
                    label="Excel Export" 
                    icon="pi pi-file-excel" 
                    severity="success" 
                />
                <Button 
                    label="PDF Export" 
                    icon="pi pi-file-pdf" 
                    severity="warning" 
                />
            </div>
        </div>
    );

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(value);
    };

    return (
        <div className="card">
            <div className="flex justify-content-between align-items-center mb-4">
                <h1 className="m-0">Sipariş Özeti</h1>
            </div>
            
            <div className="grid">
                <div className="col-12">
                    <Card title="Sipariş Trendi" className="mb-4">
                        <div>
                            <Chart type="bar" data={chartData} options={chartOptions} />
                        </div>
                    </Card>
                </div>
                
                <div className="col-12">
                    <Card>
                        <DataTable 
                            value={summaries} 
                            paginator 
                            className="p-datatable-gridlines"
                            showGridlines
                            rows={10} 
                            dataKey="id"
                            loading={loading}
                            filters={filters}
                            filterDisplay="menu"
                            globalFilterFields={['period', 'topCustomer', 'topProduct']}
                            header={header}
                            emptyMessage="Sipariş özeti bulunamadı."
                            rowsPerPageOptions={[5, 10, 25, 50, 100, 500, 1000]} 
                            tableStyle={{ minWidth: '50rem' }}
                        >
                            <Column 
                                field="period" 
                                header="Dönem" 
                                filter
                                filterElement={(options) => (
                                    <Dropdown
                                        value={options.value}
                                        options={periodOptions}
                                        onChange={(e) => options.filterCallback(e.value)}
                                        optionLabel="name"
                                        optionValue="code"
                                        placeholder="Tümü"
                                        className="p-column-filter"
                                    />
                                )}
                                sortable 
                                style={{ width: '120px' }}
                            />
                            <Column 
                                field="totalOrders" 
                                header="Toplam Sipariş" 
                                sortable 
                                style={{ width: '120px' }}
                            />
                            <Column 
                                field="completedOrders" 
                                header="Tamamlanan" 
                                sortable 
                                style={{ width: '120px' }}
                            />
                            <Column 
                                field="pendingOrders" 
                                header="Bekleyen" 
                                sortable 
                                style={{ width: '120px' }}
                            />
                            <Column 
                                field="cancelledOrders" 
                                header="İptal Edilen" 
                                sortable 
                                style={{ width: '120px' }}
                            />
                            <Column 
                                field="totalAmount" 
                                header="Toplam Tutar" 
                                body={(rowData) => formatCurrency(rowData.totalAmount)}
                                sortable 
                                style={{ width: '150px' }}
                            />
                            <Column 
                                field="averageOrderValue" 
                                header="Ortalama Sipariş Değeri" 
                                body={(rowData) => formatCurrency(rowData.averageOrderValue)}
                                sortable 
                                style={{ width: '180px' }}
                            />
                            <Column 
                                field="topCustomer" 
                                header="En Çok Sipariş Veren Müşteri" 
                                filter
                                filterPlaceholder="Müşteri ara..."
                                sortable 
                                style={{ width: '200px' }}
                            />
                            <Column 
                                field="topProduct" 
                                header="En Çok Sipariş Edilen Ürün" 
                                filter
                                filterPlaceholder="Ürün ara..."
                                sortable 
                                style={{ width: '200px' }}
                            />
                        </DataTable>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default OrderSummaryPage;
